import React, { useMemo, useState } from 'react';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
  AlertDialogOverlay, Badge, Box, Button, HStack, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal, Text, Tooltip,
  VStack, useDisclosure, useToast, Flex,
} from '@chakra-ui/react';
import {
  FiCreditCard,
  FiTag,
  FiPause,
  FiPlay,
  FiSlash,
  FiFileText,
  FiEdit2,
  FiArchive,
  FiRotateCcw,
  FiRefreshCw,
  FiRepeat,
  FiSend,
  FiMoreVertical,
} from 'react-icons/fi';
import { QueryStatus } from 'react-query';
import axios from 'axios';
import { FaFilePdf } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { NIL } from 'uuid';
import {
  HELP_DESK_TIER_LABEL, hasProducts, RecurringRevenue, RecurringRevenueStatus, frequencyLabel,
} from '../models/recurringRevenue';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';
import { previewStripeInvoicePdf } from '../utils/openPdf';
import { money } from '../utils/money';
import IconInvoice from '../assets/Icon-Invoice.svg';
import DocumentGridShell from './DocumentGridShell';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import { withSubscriptionUnit } from '../utils/subscriptionUnit'; // item 1590/1591
import useStripeSubscriptionIdentities from '../hooks/useStripeSubscriptionIdentities'; // item 1591
import StatusPill from './StatusPill';
import { useConfirm } from './ConfirmProvider';
import StripeIdChip from './StripeIdChip';
import EditRecurringRevenueModal from './EditRecurringRevenueModal';
import RecurringProductsModal from './RecurringProductsModal'; // item 1360
import EditRecurringEarnersModal from './EditRecurringEarnersModal';
import AutoPayModal from './AutoPayModal';
import useRecurringRevenueActions from '../hooks/useRecurringRevenueActions';
import { useRecurringRevenuesByProject, usePendingRecurringEnrollments } from '../hooks/useRecurringRevenues';
import useCurrentUser from '../hooks/useCurrentUser'; // item 462a
import AddRecurringInvoiceWizard from './AddRecurringInvoiceWizard'; // item 462a
import EnrollSubscriptionModal from './EnrollSubscriptionModal'; // item 737
import StripeSubDetailHover from './StripeSubDetailHover'; // item 1128

interface RecurringRevenueGridProps {
  data?: RecurringRevenue[];
  status: QueryStatus;
  // company-wide view shows the customer + project columns; the per-project section hides them
  showProjectColumns?: boolean;
  collapse?: { collapsed: boolean; onToggle: () => void };
  // item 462a — ADD button in the section header (opens the subscription wizard)
  onAdd?: () => void;
  // item 731 (Chad) — render JUST the ledger (no DocumentGridShell header/search chrome) so a
  // parent can own a single cohesive header for grouped tables.
  embedded?: boolean;
  embeddedEmptyText?: string;
}

// item 63 — the recurring-schedule grid, used both in the per-project section and on
// the company-wide Recurring Revenue Management page.
const RecurringRevenueGrid: React.FC<RecurringRevenueGridProps> = (props) => {
  const {
    data, status, showProjectColumns, onAdd, embedded, embeddedEmptyText,
  } = props;
  const history = useHistory();
  const toast = useToast();
  const {
    setStatus, setArchive, syncFromStripe, resendInvoice,
  } = useRecurringRevenueActions();
  // item 1591 — "Name (SOFTWARE_ID · friendly name)": what the Stripe subscription carries on
  // itself, or the schedule's own notes, tells a multi-unit customer's rows apart
  const identities = useStripeSubscriptionIdentities();
  const nameWithUnit = (r: RecurringRevenue) => withSubscriptionUnit(r.name ?? '', {
    identity: identities[r.stripeSubscriptionId ?? ''], headerNote: r.invoiceHeaderNote, footerNote: r.invoiceFooterNote,
  });
  // item 731 (Chad) — re-email the subscription's latest Stripe invoice to the client
  const doResend = async (r: RecurringRevenue) => {
    try {
      await resendInvoice(r.id);
      toast({ status: 'success', title: 'Invoice re-sent', description: 'Stripe emailed the latest invoice to the client.' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not resend', description: String(e?.response?.data ?? 'Stripe could not resend the invoice.') });
    }
  };
  // item 754 (Chad) — ONE-OFF: with the client's permission, charge the sub's open invoice(s)
  // against the bank on file right now (e.g. invoices issued before auto-pay was enrolled).
  // item 889 — charges ALL open invoices, not just the latest.
  const confirmDialog = useConfirm();
  const chargeOpenInvoice = async (r: RecurringRevenue) => {
    const ok = await confirmDialog(
      'This debits the client\'s bank on file for ALL of this subscription\'s open invoices, one time. '
      + 'Confirm the client has given permission for this charge.',
      { title: 'Charge bank on file?', confirmLabel: 'Client approved — charge now', cancelLabel: 'Not yet' },
    );
    if (!ok) return;
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const res = await axios.post(`${apiUrl}payment/charge-sub-invoice/${r.id}`);
      const n = res.data?.charged ?? 1;
      toast({
        status: 'success',
        title: 'Charge started',
        description: `ACH debit initiated for ${n} open invoice${n === 1 ? '' : 's'} — marks paid when it settles.`,
      });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not charge', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const editModal = useDisclosure();
  const [editing, setEditing] = useState<RecurringRevenue | undefined>(undefined);
  const openEdit = (r: RecurringRevenue) => { setEditing(r); editModal.onOpen(); };
  // #25 — edit the recurring commission split (who earns, going forward)
  const earnersModal = useDisclosure();
  const [editingEarners, setEditingEarners] = useState<RecurringRevenue | undefined>(undefined);
  const openEarners = (r: RecurringRevenue) => { setEditingEarners(r); earnersModal.onOpen(); };
  // item 296 — cancelling a subscription is irreversible (the record stays forever per
  // item 170, but billing + monthly rep commissions stop and there is no un-cancel), so
  // it gets an explicit confirm instead of firing straight off the row menu.
  const [cancelling, setCancelling] = useState<RecurringRevenue | undefined>(undefined);
  const [productsFor, setProductsFor] = useState<RecurringRevenue | undefined>(undefined); // item 1360
  // item 298 — auto-pay enrollment (real Stripe subscriptions only)
  const [autoPayId, setAutoPayId] = useState<string | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const confirmCancel = () => {
    if (cancelling) setStatus(cancelling.id, RecurringRevenueStatus.Cancelled);
    setCancelling(undefined);
  };

  // item 724 (Chad) — migrated to the shared LedgerTable (sortable columns + search + consistent look).
  // item 743 (Chad) — width pass so the grid NEVER scrolls sideways: fixed compact widths, nowrap
  // money/dates, NAME flexes and truncates with a hover for the full subscription name.
  const columns: LedgerColumn<RecurringRevenue>[] = useMemo(() => {
    const cols: LedgerColumn<RecurringRevenue>[] = [
      {
        // item 1445 (Chad: "a pass on all the ledgers … the name at the bottom under the other items like the portal") —
        // SUBSCRIPTION = name bold (two lines, full name on hover), customer · project as subtext
        // outside a project. The separate CUSTOMER / PROJECT columns are gone; this is the one
        // flexible column (300px floor).
        // item 1447 — the subscription NAME rides the full-width rowSubline (never truncates); this
        // cell carries the customer (the short identity) and the item-1128 Stripe hover.
        key: 'name',
        header: 'SUBSCRIPTION',
        width: '180px',
        sortValue: (r) => r.name ?? '',
        // item 1128 (Chad) — Stripe-backed subs hover their LIVE Stripe line detail
        // (products, amounts, description, start) instead of just the full name
        render: (r) => {
          const label = <Text fontWeight="semibold" noOfLines={1} title={r.customerName ?? r.name ?? ''}>{r.customerName || r.name || '—'}</Text>;
          return (r.stripeSubscriptionId ?? '').startsWith('sub_')
            ? <StripeSubDetailHover recurringId={r.id}>{label}</StripeSubDetailHover>
            : label;
        },
      },
    ];
    cols.push({
      key: 'amount', header: 'AMOUNT', align: 'right', width: '104px', sortValue: (r) => r.amount ?? 0, render: (r) => <Text whiteSpace="nowrap">{money(r.amount ?? 0)}</Text>,
    });
    cols.push({
      key: 'frequency', header: 'FREQUENCY', width: '104px', sortValue: (r) => r.frequency, render: (r) => <Text noOfLines={1}>{frequencyLabel(r.frequency, r.customIntervalDays)}</Text>,
    });
    // item 1360 — WHAT the sub delivers (RNWY / SwitchPilot / Help Desk·tier / Third-party),
    // human-verified; UNTAGGED = RNWY sees "unknown". Click the cell to set it.
    cols.push({
      key: 'products',
      header: 'PRODUCTS',
      width: '150px',
      sortValue: (r) => (hasProducts(r) ? 1 : 0) + (r.productsVerifiedDate ? 1 : 0),
      render: (r) => (
        <HStack spacing={1} flexWrap="wrap" cursor="pointer" onClick={(e) => { e.stopPropagation(); setProductsFor(r); }} title="Set products">
          {!hasProducts(r) && !r.productsVerifiedDate && <Badge colorScheme="orange" variant="solid" fontSize="0.55rem">UNTAGGED</Badge>}
          {!hasProducts(r) && r.productsVerifiedDate && <Badge colorScheme="gray" fontSize="0.55rem">NONE</Badge>}
          {r.isRnwy && <Badge colorScheme="purple" fontSize="0.55rem">RNWY</Badge>}
          {r.isSwitchPilot && <Badge colorScheme="blue" fontSize="0.55rem">SP</Badge>}
          {r.isHelpDesk && <Badge colorScheme="teal" fontSize="0.55rem">{`HD${r.helpDeskTier ? ` · ${HELP_DESK_TIER_LABEL[r.helpDeskTier]}` : ''}`}</Badge>}
          {r.isThirdParty && <Badge colorScheme="gray" fontSize="0.55rem">3P</Badge>}
          {hasProducts(r) && !r.productsVerifiedDate && <Badge colorScheme="yellow" variant="outline" fontSize="0.55rem">VERIFY</Badge>}
          {r.productsVerifiedDate && <Badge colorScheme="green" variant="outline" fontSize="0.55rem">✓</Badge>}
        </HStack>
      ),
    });
    cols.push({
      key: 'status',
      header: 'STATUS',
      width: '112px',
      sortValue: (r) => r.status,
      // item 769 (Chad) — an auto-pay-enrolled sub says so right in the ledger
      render: (r) => (
        <VStack align="start" spacing={0.5}>
          <StatusPill label={RecurringRevenueStatus[r.status]} isVoid={r.isVoid} minW="92px" />
          {r.autoPayEnrolled && r.status === RecurringRevenueStatus.Active && (
            /* item 1341 — hover the badge for the typed authorization note */
            <Tooltip label={r.autoPayAuthorizationMemo || undefined} hasArrow isDisabled={!r.autoPayAuthorizationMemo}>
              <Badge colorScheme="green" variant="subtle" fontSize="0.55rem" borderRadius="sm" cursor={r.autoPayAuthorizationMemo ? 'help' : undefined}>AUTO-PAY</Badge>
            </Tooltip>
          )}
          {r.autoPayEnrolled && r.status === RecurringRevenueStatus.Active && r.autoPayAuthorizationMemo && (
            <Text fontSize="0.6rem" opacity={0.55} noOfLines={1} maxW="112px" title={r.autoPayAuthorizationMemo}>
              {r.autoPayAuthorizationMemo}
            </Text>
          )}
        </VStack>
      ),
    });
    cols.push({
      key: 'nextBill', header: 'NEXT BILL', width: '100px', sortValue: (r) => r.nextBillDate ?? '', render: (r) => <Text whiteSpace="nowrap">{r.nextBillDate ? SqlDateTimeToDisplay(r.nextBillDate) : '—'}</Text>,
    });
    // item 706/731 — the latest Stripe ACH charge (item 581 confirmation) + the copyable Stripe
    // subscription id, right in the ledger; "—" until the first invoice.paid.
    cols.push({
      key: 'stripe',
      header: 'STRIPE',
      width: '140px',
      sortValue: (r) => r.stripeLastPaymentDate ?? '',
      render: (r) => (
        <VStack align="start" spacing={0}>
          {r.stripeLastPaymentDate && (
            <>
              <Text fontSize="sm" fontWeight="medium">
                {money(r.stripeLastPaymentAmount ?? 0)}
                {' '}
                <Text as="span" fontSize="xs" color="green.400">ACH</Text>
              </Text>
              <Text fontSize="xs" color="gray.500">{SqlDateTimeToDisplay(r.stripeLastPaymentDate)}</Text>
            </>
          )}
          {/* item 887 (Chad) — an invoice ISSUED but not yet paid: show it (with its
              transaction id below) instead of a misleading "no charge yet" */}
          {!r.stripeLastPaymentDate && r.lastChargeIssuedDate && (
            <>
              <Text fontSize="sm" fontWeight="medium">
                {money(r.lastChargeAmount ?? 0)}
                {' '}
                <Text as="span" fontSize="xs" color="orange.400">INVOICED</Text>
              </Text>
              <Text fontSize="xs" color="gray.500">
                {SqlDateTimeToDisplay(r.lastChargeIssuedDate)}
                {' · awaiting payment'}
              </Text>
            </>
          )}
          {!r.stripeLastPaymentDate && !r.lastChargeIssuedDate && (
            <Text fontSize="xs" color="gray.500">no charge yet</Text>
          )}
          {(r.stripeSubscriptionId ?? '').startsWith('sub_') && (
            <StripeIdChip id={r.stripeSubscriptionId} label="Subscription id" />
          )}
          {/* item 740 (Chad Q4) — Stripe owns its invoice numbering; Basix records the
              transaction ids instead. Latest invoice ref (in_…) copyable right here.
              item 887: fall back to the ISSUED charge's id before the payment clears. */}
          {(r.stripeLastInvoiceId || r.lastChargeStripeInvoiceId) && (
            <HStack spacing={1}>
              <StripeIdChip id={r.stripeLastInvoiceId || r.lastChargeStripeInvoiceId} label="Latest invoice id" />
              {/* item 1424 (Chad) — the latest Stripe invoice PDF, one click */}
              <IconButton
                aria-label="Latest Stripe invoice PDF"
                title="Latest Stripe invoice PDF"
                size="xs"
                variant="ghost"
                icon={<FaFilePdf />}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); previewStripeInvoicePdf((r.stripeLastInvoiceId || r.lastChargeStripeInvoiceId) as string).catch(() => undefined); }}
              />
            </HStack>
          )}
        </VStack>
      ),
    });
    return cols;
  }, []);

  // the per-row ⋮ menu (edit / earners / pause-resume / auto-pay / cancel / archive / restore)
  const rowActions = (r: RecurringRevenue) => (
    <Menu>
      <MenuButton fontSize="sm" _hover={{ fontWeight: 'bold', color: 'brand.100' }}>
        <FiMoreVertical />
      </MenuButton>
      <Portal>
        <MenuList fontSize="sm">
          {r.isArchive ? (
            <MenuItem icon={<FiRotateCcw />} onClick={() => setArchive(r.id, false)}>RESTORE</MenuItem>
          ) : (
            <>
              {r.status !== RecurringRevenueStatus.Cancelled && (
                <MenuItem icon={<FiEdit2 />} onClick={() => openEdit(r)}>EDIT DETAILS</MenuItem>
              )}
              {/* item 1360 — set/verify what the sub delivers (feeds the RNWY entitlements API) */}
              <MenuItem icon={<FiTag />} onClick={() => setProductsFor(r)}>PRODUCTS (RNWY / SP / HD / 3P)</MenuItem>
              {r.status !== RecurringRevenueStatus.Cancelled && (
                <MenuItem icon={<FiEdit2 />} onClick={() => openEarners(r)}>EDIT COMMISSION SPLIT</MenuItem>
              )}
              {/* item 1424b (Chad: "still no preview under subs or project/subs") — Stripe's own PDF
                  of the latest invoice, from the row menu (the STRIPE column is too narrow to trust) */}
              {(r.stripeLastInvoiceId || r.lastChargeStripeInvoiceId) && (
                <MenuItem icon={<FaFilePdf />} onClick={() => previewStripeInvoicePdf((r.stripeLastInvoiceId || r.lastChargeStripeInvoiceId) as string).catch(() => undefined)}>LATEST INVOICE (PDF)</MenuItem>
              )}
              {r.status === RecurringRevenueStatus.Active && (
                <MenuItem icon={<FiPause />} onClick={() => setStatus(r.id, RecurringRevenueStatus.Paused)}>PAUSE</MenuItem>
              )}
              {r.status === RecurringRevenueStatus.Paused && (
                <MenuItem icon={<FiPlay />} onClick={() => setStatus(r.id, RecurringRevenueStatus.Active)}>RESUME</MenuItem>
              )}
              {r.status === RecurringRevenueStatus.Active && (r.stripeSubscriptionId ?? '').startsWith('sub_') && (
                <MenuItem icon={<FiCreditCard />} onClick={() => setAutoPayId(r.id)}>AUTO-PAY</MenuItem>
              )}
              {r.status !== RecurringRevenueStatus.Cancelled && (r.stripeSubscriptionId ?? '').startsWith('sub_') && (
                <>
                  <MenuItem icon={<FiSend />} onClick={() => doResend(r)}>RESEND INVOICE</MenuItem>
                  {/* item 754 (Chad) — one-off charge of the open invoice, with permission */}
                  <MenuItem icon={<FiCreditCard />} onClick={() => chargeOpenInvoice(r)}>CHARGE BANK ON FILE</MenuItem>
                </>
              )}
              {(r.stripeSubscriptionId ?? '').startsWith('sub_') && (
                <MenuItem icon={<FiRefreshCw />} onClick={() => syncFromStripe(r.id)}>SYNC FROM STRIPE</MenuItem>
              )}
              {r.status !== RecurringRevenueStatus.Cancelled && (
                <MenuItem icon={<FiSlash />} onClick={() => setCancelling(r)}>STOP (CANCEL)</MenuItem>
              )}
              <MenuItem icon={<FiArchive />} onClick={() => setArchive(r.id, true)}>ARCHIVE</MenuItem>
            </>
          )}
          {r.sourceInvoiceId && (
            <MenuItem icon={<FiFileText />} onClick={() => history.push(`/invoice/${r.sourceInvoiceId}`)}>OPEN SOURCE INVOICE</MenuItem>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );

  // item 905 (Chad) — "clear out subscriptions that are canceled": cancelled records stay
  // forever (item 170, they're the history) but they HIDE from the ledger by default; a
  // counted toggle brings them back. Archived rows keep their existing behavior.
  const [showCancelled, setShowCancelled] = useState(false);
  const cancelledCount = (data ?? []).filter((r) => !r.isArchive && r.status === RecurringRevenueStatus.Cancelled).length;
  const visibleRows = (data ?? []).filter((r) => showCancelled || r.isArchive || r.status !== RecurringRevenueStatus.Cancelled);

  const table = (
    <>
      {cancelledCount > 0 && (
        <Flex justify="flex-end" mb={1}>
          <Button size="xs" variant="ghost" color="gray.500" onClick={() => setShowCancelled(!showCancelled)}>
            {showCancelled ? `Hide cancelled (${cancelledCount})` : `Show cancelled (${cancelledCount})`}
          </Button>
        </Flex>
      )}
      <LedgerTable<RecurringRevenue>
        defaultSort={{ key: 'started', dir: -1, value: (r) => r.startDate ?? '' }} // item 1331 — newest sub on top by default
        rows={visibleRows}
        columns={columns}
        // item 1590 (Chad: "show the friendly name under the Sub name in parentheses") — read from the
        // schedule's memo/footer note ("Friendly Name: …"); front-end only, nothing new stored
        rowSubline={(r) => <LedgerSubline name={nameWithUnit(r)} meta={showProjectColumns ? [r.projectName] : []} />}
        rowId={(r) => r.id}
        rowActions={rowActions}
        rowActionsHeader=""
        selectable={false}
        searchText={(r) => `${nameWithUnit(r)} ${r.customerName ?? ''} ${r.projectName ?? ''}`}
        searchPlaceholder="Search subscriptions…"
        onRowClick={(r) => { if (!r.isArchive) openEdit(r); }}
        emptyText={embedded ? (embeddedEmptyText ?? 'No subscriptions.') : 'No subscriptions.'}
      />
    </>
  );

  return (
    <>
      {embedded ? table : (
        <DocumentGridShell
          heading="Subscriptions"
          icon={IconInvoice}
          onAdd={onAdd}
          collapsible={!!props.collapse}
          collapsed={props.collapse?.collapsed}
          onToggleCollapse={props.collapse?.onToggle}
          status={status}
          data={status === 'success' ? visibleRows : undefined}
        >
          {table}
        </DocumentGridShell>
      )}
      <EditRecurringRevenueModal isOpen={editModal.isOpen} onClose={editModal.onClose} recurring={editing} />
      <RecurringProductsModal recurring={productsFor} isOpen={!!productsFor} onClose={() => setProductsFor(undefined)} />
      <EditRecurringEarnersModal
        isOpen={earnersModal.isOpen}
        onClose={earnersModal.onClose}
        recurringId={editingEarners?.id}
        recurringName={editingEarners?.name}
      />
      <AutoPayModal recurringId={autoPayId} onClose={() => setAutoPayId(null)} />
      {/* item 296 — cancel-subscription confirm */}
      <AlertDialog
        isOpen={!!cancelling}
        leastDestructiveRef={cancelRef}
        onClose={() => setCancelling(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel this subscription?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={2}>
                {cancelling ? `${cancelling.customerName} — recurring billing stops permanently.` : ''}
              </Text>
              <Text fontSize="sm" opacity={0.75}>
                Future invoices and monthly rep commissions stop from now on. The schedule
                and its full history are kept (nothing is deleted), but a cancelled
                subscription cannot be restarted — re-enroll the customer to begin a new one.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setCancelling(undefined)}>
                Keep it
              </Button>
              <Button colorScheme="red" ml={3} onClick={confirmCancel}>
                Cancel subscription
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

RecurringRevenueGrid.defaultProps = {
  data: undefined,
  showProjectColumns: false,
  collapse: undefined,
  onAdd: undefined,
  embedded: false,
  embeddedEmptyText: undefined,
};

export default RecurringRevenueGrid;

// item 63a — the per-project Recurring section (self-fetches by project id).
// item 462a — creation lives here too: ADD opens the item-448 wizard with the
// project preselected + locked, so a subscription starts without leaving the
// project. Admin-gated (subscriptions are financial; Team is read-only here).
export const ProjectRecurringSection: React.FC<{
  projectId: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, collapse }) => {
  const { data, status } = useRecurringRevenuesByProject(projectId === NIL ? undefined : projectId);
  const { isAdmin } = useCurrentUser();
  const addWizard = useDisclosure();
  return (
    <>
      <RecurringRevenueGrid
        data={data?.index}
        status={status}
        collapse={collapse}
        onAdd={isAdmin && projectId !== NIL ? addWizard.onOpen : undefined}
      />
      <AddRecurringInvoiceWizard
        isOpen={addWizard.isOpen}
        onClose={addWizard.onClose}
        lockedProjectId={projectId}
      />
    </>
  );
};

// item 737 (Chad) — surface any sold-but-not-enrolled subscription for THIS project, with an
// Enroll button. item 1266 (Chad: "the credits and pending enroll feel out of place") — MOVED
// out of the Subscriptions section into the project's top notice box ("outside the Basix Six");
// same nudges, same Enroll wizard, new home.
export const ProjectPendingEnrollments: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { isAdmin } = useCurrentUser();
  const { data: pendingData } = usePendingRecurringEnrollments();
  const [enrollProposalId, setEnrollProposalId] = useState<string | undefined>(undefined);
  const pendingHere = (pendingData?.index ?? [])
    .filter((r) => r.projectId === projectId && !r.declined);
  if (!isAdmin || projectId === NIL || pendingHere.length === 0) return null;
  return (
    <>
      {pendingHere.map((r) => (
        // item 737 (Chad) — the enroll prompt is deliberately NOT a peer section row: it's a
        // TRANSIENT callout (here only until the sub is enrolled), so it wears a dashed orange
        // border + a rotated PENDING rubber stamp (FinalizedStamp's grammar) to read as "action
        // needed", outside the project hierarchy. The Enroll button still matches the ADD buttons
        // (xs · minW 92px) and sits in the same column (trailing 18px chevron spacer).
        <Box
          key={r.proposalId}
          my={1}
          px={{ base: 3, md: 4 }}
          py={2}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="orange.400"
          borderRadius="lg"
          bg="rgba(251, 146, 60, 0.06)"
        >
          <HStack spacing={3}>
            <Box
              display="inline-block"
              transform="rotate(-6deg)"
              borderWidth="2px"
              borderColor="orange.400"
              color="orange.400"
              borderRadius="sm"
              px={1.5}
              lineHeight="1.4"
              fontSize="0.55rem"
              fontWeight="extrabold"
              letterSpacing="0.14em"
              opacity={0.9}
              userSelect="none"
              flexShrink={0}
            >
              PENDING
            </Box>
            <Text fontSize="sm" fontWeight="semibold" flexShrink={0}>Subscription to enroll</Text>
            <Text fontSize="sm" opacity={0.65} noOfLines={1} flex={1} minW={0}>
              {`${r.proposalTitle} (${r.proposalNumber}) — sold, not billing yet`}
            </Text>
            <Button
              size="xs"
              minW="92px"
              colorScheme="orange"
              leftIcon={<FiRepeat />}
              flexShrink={0}
              onClick={() => setEnrollProposalId(r.proposalId)}
            >
              Enroll
            </Button>
            <Box w="18px" flexShrink={0} />
          </HStack>
        </Box>
      ))}
      <EnrollSubscriptionModal
        proposalId={enrollProposalId}
        isOpen={!!enrollProposalId}
        onClose={() => setEnrollProposalId(undefined)}
      />
    </>
  );
};

ProjectRecurringSection.defaultProps = {
  collapse: undefined,
};
